import type { TFunction } from "i18next";
import type { AttributeSchemaEntry } from "../../../api/catalog";

/** The administrator's own wording for `locale`, then English, then nothing. */
function ownLabel(labels: Record<string, string> | undefined, locale: string): string | undefined {
  return labels?.[locale]?.trim() || labels?.en?.trim() || undefined;
}

/** locale -> label with blanks dropped; `{}` when nothing was filled in. */
function trimLabels(labels: Record<string, string> | undefined): Record<string, string> {
  return Object.fromEntries(
    Object.entries(labels ?? {}).map(([l, v]) => [l, v.trim()]).filter(([, v]) => v !== ""),
  );
}

/** CSV as typed in the editor → distinct, trimmed option values. */
export function parseOptions(csv: string): string[] {
  return [...new Set(csv.split(",").map((s) => s.trim()).filter(Boolean))];
}

/**
 * The label an attribute shows in `locale`: the administrator's own wording
 * first, then English, then the attr.* i18n key the built-ins use, then the
 * raw key. Shared by the category schema editor and the product editor so both
 * name an attribute the same way.
 */
export function attributeLabel(
  entry: Pick<AttributeSchemaEntry, "key" | "labelKey" | "labels">,
  locale: string,
  t: TFunction,
): string {
  const own = ownLabel(entry.labels, locale);
  if (own) return own;
  const fallbackKey = entry.labelKey ?? entry.key;
  return t(`attr.${fallbackKey}`, fallbackKey);
}

/**
 * The label a select/multiselect option shows in `locale`, in the same order
 * as attributeLabel: the administrator's own wording, then English, then the
 * industry.* keys the seeded options happen to have, then the raw value. Only
 * the seeded values have i18n keys, so without an admin label an option added
 * later would read raw in all ten languages.
 */
export function optionLabel(
  entry: Pick<AttributeSchemaEntry, "optionLabels">,
  value: string,
  locale: string,
  t: TFunction,
): string {
  return ownLabel(entry.optionLabels?.[value], locale) ?? t(`industry.${value}`, value);
}

export type SchemaRow = {
  key: string;
  labelKey: string;
  /** locale -> label; the editor writes the current UI language into this. */
  labels: Record<string, string>;
  type: "string" | "number" | "select" | "multiselect" | "boolean";
  unit: string;
  options: string; // CSV in the editor
  /** option value -> (locale -> label), same per-locale shape as `labels`. */
  optionLabels: Record<string, Record<string, string>>;
  filterable: boolean;
};

export const emptyRow = (): SchemaRow => ({ key: "", labelKey: "", labels: {}, type: "string", unit: "", options: "", optionLabels: {}, filterable: false });

/** AttributeSchemaEntry[] (from CategoryDetail) → editor rows. */
export function toRows(schema: AttributeSchemaEntry[] | null | undefined): SchemaRow[] {
  return (schema ?? []).map((e) => ({
    key: e.key ?? "",
    labelKey: e.labelKey ?? "",
    labels: e.labels ?? {},
    type: (e.type as SchemaRow["type"]) ?? "string",
    unit: e.unit ?? "",
    options: (e.options ?? []).join(", "),
    optionLabels: e.optionLabels ?? {},
    filterable: e.filterable ?? false,
  }));
}

/** editor rows → AttributeSchemaEntry[] for the API. Drops blank-key rows; trims; options CSV→array only for select. */
export function toSchema(rows: SchemaRow[]): AttributeSchemaEntry[] {
  return rows
    .filter((r) => r.key.trim() !== "")
    .map((r) => {
      const e: AttributeSchemaEntry = { key: r.key.trim() };
      if (r.labelKey.trim()) e.labelKey = r.labelKey.trim();
      const labels = trimLabels(r.labels);
      if (Object.keys(labels).length > 0) e.labels = labels;
      if (r.type) e.type = r.type;
      if (r.unit.trim()) e.unit = r.unit.trim();
      if (r.type === "select" || r.type === "multiselect") {
        const options = parseOptions(r.options);
        e.options = options;
        // Keyed by option value, so editing an option out of the CSV takes its
        // now-orphaned labels with it instead of leaving them in the schema.
        const optionLabels: Record<string, Record<string, string>> = {};
        for (const o of options) {
          const ol = trimLabels(r.optionLabels[o]);
          if (Object.keys(ol).length > 0) optionLabels[o] = ol;
        }
        if (Object.keys(optionLabels).length > 0) e.optionLabels = optionLabels;
      }
      if (r.filterable) e.filterable = true;
      return e;
    });
}

/** Returns the first duplicate/blank-after-filter problem, or null if rows are valid. */
export function schemaError(rows: SchemaRow[]): string | null {
  const keys = rows.map((r) => r.key.trim()).filter(Boolean);
  const dup = keys.find((k, i) => keys.indexOf(k) !== i);
  return dup ? dup : null; // caller turns non-null into a localized "duplicate key" message
}
